import React, { useState, useRef, useEffect } from "react";
import { actions } from "react-redux-form";
import { Button, Col, Row, DropdownItem, Dropdown, DropdownToggle, Input, DropdownMenu, Modal, ModalBody, ModalFooter, ModalHeader, Alert } from "reactstrap";
import { compose, withHandlers, withPropsOnChange } from "recompose";
import shortid from "shortid";
import { Trans } from "react-i18next";
import { FormGroup, required, Page, onMount, withReload, withLocalForm } from "components/common";
import { withXeroErrors, LineItemsView, withContactSearch, withDocumentRenderer, withOptions, Attachments, Payment } from "components";
import { BillCreditNoteDocument, InvoiceCreditNoteDocument } from "components/documents";
import { withOrganisationContextConsumer } from "contexts/OrganisationContext";
import { invoiceSDKPost } from "components/common/ZatcaSendInvoice";
import { ZatcaClient, ApiClient, useConfirmModal } from "services";
import { getStatus, getType } from "components/common/getStatus";
import { format_reference, formatDate, formatDateTime } from "services/util";
import { Link } from "react-router-dom";
import BackButton from "components/refactoring/BackButton";
import { getOutlineStyle } from "pages/UserList/Edit";
import useNoTaxHandler from "./useNoTaxHandler";
import SmallCreditNote from "components/documents/SmallCreditNote";
import SimpleSmallCreditNote from "components/documents/SimpleSmallCreditNote";
import HistoryComponent from "components/ShowHistoryComponent";
import { NotificationManager } from "react-notifications";
import { useZatcaAppsChecker } from "./useZatcaAppsChecker";
import { useTaxExemptChecker } from "./useTaxExemptChecker";
import { ErrorDetailsComponent } from "components/common/ErrorDetailsComponent";
import { useLocation } from "react-router";
import { useQuery } from "react-query";

const ShareCell = withDocumentRenderer({
  onDownload: () => (getDocument, item) =>
    getDocument(
      {
        item,
      },
      `creditnote_${item.CreditNoteNumber}.pdf`
    ),
})(({ DocumentRenderer, ShareDocument, i, componentDoc, selectedOrganisation, simplified, validation, setDownloadHandler }) => (
  <td className="text-end text-left">
    <ShareDocument item={i} wtsp={true} simplified={simplified} validation={validation} setDownloadHandler={setDownloadHandler} />
    <DocumentRenderer component={componentDoc} selectedOrganisation={selectedOrganisation} />
  </td>
));

const PrintCell = withDocumentRenderer({
  onDownload: () => (getDocument, item) =>
    getDocument(
      {
        item,
      },
      `small_${item.CreditNoteNumber}.pdf`,
    ),
})(({ PrintDocument, DocumentRenderer, i, componentDoc, selectedOrganisation, simplified }) => (
  <td className="text-end text-left">
    <PrintDocument label="Small CreditNote" item={i} is_small={true} simplified={simplified} />
    <DocumentRenderer component={componentDoc} selectedOrganisation={selectedOrganisation} />
  </td>
));


const DownloadCell = withDocumentRenderer({
  onDownload: () => (getDocument, item) =>
    getDocument(
      {
        item,
      },
      `creditnote_${item.CreditNoteNumber}.pdf`,
    ),
  type: "SALES_CREDITNOTE",
})(({ DownloadDocument, DocumentRenderer, i, componentDoc, type, selectedOrganisation, simplified, validation, setDownloadHandler }) => (
  <>
    <DownloadDocument item={i} simplified={simplified} validation={validation} setDownloadHandler={setDownloadHandler} className="mr-1" />
    <DocumentRenderer component={componentDoc} type={type} selectedOrganisation={selectedOrganisation} />
  </>
));

const EmailCell = withDocumentRenderer({
  onDownload: () => (getDocument, item) =>
    getDocument(
      {
        item,
      },
      `creditnote_${item.CreditNoteNumber}.pdf`,
    ),
})(({ EmailDocument, DocumentRenderer, i, componentDoc, type, selectedOrganisation, simplified, validation, setDownloadHandler }) => (
  <>
    <EmailDocument type={"CREDIT_NOTE"} item={i} simplified={simplified} validation={validation} setDownloadHandler={setDownloadHandler} className="mr-1" />
    <DocumentRenderer component={componentDoc} type={type} selectedOrganisation={selectedOrganisation} />
  </>
));

export const ViewCreditNote = ({
  item,
  credHistory,
  LocalForm,
  onSubmit,
  xero,
  dispatch,
  handlePaymentAdded,
  handleAttachmentsChanged,
  XeroErrors,
  organisation,
  t,
  payable,
  DocumentRenderer,
  CreditNoteOptions,
  selectedOrganisation,
  reload,
}) => {

  const cStyle = {
    status: {
      style: { fontWeight: 'bold', fontSize: '1.3em', color: '#72B566' },
    },
    common: {
      className: "pt-3 pb-3",
      xs: false,
      md: 3,
    },
  };

  const {data:print_templates} = useQuery("print_templates", () => xero.get("all/invoice_settings").get("data").get("invoice_layout_settings"), {
  });

  const [items, setItems] = useState({ ...item, Reference: format_reference(item.Reference) })

  const zatca = ZatcaClient()
  const client = ApiClient()

  const location = useLocation();
  const urlParams = new URLSearchParams(location?.search);
  const auto_report = urlParams.get("auto_report");

  const isZatcaSubscribed = selectedOrganisation?.subscribed_features?.hesaabmodule?.includes('wpconnect')
  const pageTakeOver = useRef(null);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [isPaymentVisible, setPaymentVisibility] = useState(false);
  const [useSimpleType, setSimpleType] = useState(item.DocumentType === 'simplified');
  const [reportedList, setReportedList] = useState();
  const [creditNoteData, setZatcaData] = useState(null);

  const CanReport = ((item.Type === "ACCREC" || item.Type === "ACCRECCREDIT") && (item.Status === "AUTHORISED" || item.Status === "PAID"))
  const IsReported = reportedList?.reported || reportedList?.cleared

  const [selectedZatcaApp, setSelectedZatcaApp] = useState();
  const { preGetInvoiceXML, preHandleSDKPost, NoTaxComponent, isSending, refreshFlag, setDocument, zatcaApps } = useNoTaxHandler(t, "creditnote", reload)
  const [checkZeroTaxLines, TaxExemptComponent] = useTaxExemptChecker(t, isZatcaSubscribed, item, reload, CanReport && !IsReported && !auto_report)
  const [selectApp, ZatcaAppsComponent] = useZatcaAppsChecker(t, isZatcaSubscribed, setSelectedZatcaApp, CanReport && !IsReported)

  const [, toggleResend, MainResendModal, setCallback] = useConfirmModal()
  const [errorWindow, setErrorWindow] = useState(false);


  const docType = "creditnote"
  const creditNoteType = useSimpleType ? "simplified" : "standard";
  const formattedType = creditNoteType.charAt(0).toUpperCase() + creditNoteType.slice(1) + " CreditNote";
  const selectedPrintCell = useSimpleType ? SimpleSmallCreditNote : SmallCreditNote;

  // Missings (Reason & allocated invoice) Handling code
  const [missingModalOpen, setMissingModalOpen] = useState(false)
  const missingModalToggle = () => setMissingModalOpen(!missingModalOpen)
  const [missingData, setMissingData] = useState({})
  const [callbackFunc, setCallbackFunc] = useState();
  const hasZatcaAccess = (selectedOrganisation?.country?.toLowerCase() === 'sa' && selectedOrganisation?.zatca_phase_one_enabled) || isZatcaSubscribed



  const validateDownload = () => {
    if (!CanReport || !hasZatcaAccess) {
      return true
    }
    else {
      return checkZeroTaxLines() && selectApp(selectedZatcaApp)
    }
  }

  const setDownloadHandler = (func) => {
    setCallbackFunc(() => func);
  }

  const preLocalChecking = (callback = undefined, reporting) => {
    setZatcaData({ ...creditNoteData, ...item, reporting: reporting })
    if (!isPayable && hasZatcaAccess && (!items.Reason || !(items.Allocations[0]?.Invoice || items.AllocationInvoice))) {
      missingModalToggle()
    } else {
      if (callback) {
        callback({ ...creditNoteData, ...item, reporting: reporting })
      } else {
        return validateDownload()
      }
    }
    return false
  }

  const handleMissingSave = () => {
    client.post(`xero_actions/${selectedOrganisation.id}/save_missing_credit_note`, { ...missingData, CreditNoteID: items.CreditNoteID })
      .then((resp) => {
        NotificationManager.success("Successfully saved!")
        missingModalToggle()
        reload()
      }
      ).catch((err) => {
        NotificationManager.error(t("Failed to save!"))
      })
  }

  const checkZatcaResend = (callback) => {
    if (reportedList.reported || reportedList.cleared) {
      toggleResend()
      setCallback(() => callback)
    }
    else {
      callback()
    }
  }

  useEffect(() => {
    if (!isSending && !IsReported && (zatcaApps && auto_report && reportedList && CanReport && selectedOrganisation.zatca_auto_send?.enabled && hasZatcaAccess)) {
      checkZatcaResend(() => preLocalChecking((updated_data) => preHandleSDKPost({ ...updated_data, reporting: true }), true))
    }
  }, [reportedList, zatcaApps])


  useEffect(() => {
    if (isPaymentVisible && pageTakeOver.current) {
      setTimeout(() => {
        const fullPageHeight = document.body.scrollHeight;
        window.scrollTo({
          top: fullPageHeight,
          behavior: 'smooth',
        });
      }, 200);
    }
  }, [isPaymentVisible]);


  const togglePaymentVisibility = () => {
    setPaymentVisibility(!isPaymentVisible);
  };

  const toggleDropdown = () => {
    setDropdownOpen(!dropdownOpen);
  };

  useEffect(() => {
    if (items) {
      const mappedData = invoiceSDKPost(items, docType, creditNoteType);
      setZatcaData(mappedData);
      setDocument(items)
    }
  }, [items, creditNoteType, creditNoteType]);

  const accPayCheck = /purchases|payable|accounts_payable/g

  const isPayable = accPayCheck.test(window.location.href);

  const view_link = isPayable ? "purchases" : "accounts_receivable"

  useEffect(() => {
    const fetchData = async () => {
      try {
        const responseSDK = await zatca.get(`reports/${item?.CreditNoteID}/single`);
        const reportedList = responseSDK.data;
        setReportedList(reportedList);
      } catch (error) {
        console.error("Error fetching data:", error);
      }
    };

    fetchData();
  }, [item?.CreditNoteID, refreshFlag]);

  const status = (() => {
    if (reportedList?.reported) {
      return "Reported";
    } else if (reportedList?.cleared) {
      return "Cleared";
    } else if (reportedList?.complied) {
      return "Complied";
    } else {
      return false;
    }
  })();

  const componentDoc = isPayable ? BillCreditNoteDocument :
    (
      useSimpleType ? ({ ...props }) => <InvoiceCreditNoteDocument {...props} name="Simplified Credit Note" /> :
        ({ ...props }) => <InvoiceCreditNoteDocument {...props} name="Taxed Credit Note" />
    )

  return (
    <div>
      <Page
        title={t(`View Credit Note {{ CreditNoteNumber }}`,
          { CreditNoteNumber: item.CreditNoteNumber, })}
        translate={false}
      >
        <div className="my-2 row">
          <div className="col-6">
            <BackButton />
          </div>
          {item.Type === "ACCRECCREDIT" && <div className="col-6 text-right d-flex justify-content-end align-items-center">
            <div className="inline-block mx-1">
              {!!print_templates?.length && !!item.CreditNoteID && (
                <div>
                  <FormGroup>
                    <Input
                      type="select"
                      className="py-0 text-center"
                      onChange={(e) => setItems({ ...items, template_id: Number(e.target.value) })}
                      value={items.template_id}
                    >
                      <option value="">-- {t('Select Template')} --</option>
                      {print_templates.map((template) => (
                        <option value={template.id} key={template.id}>{template.name}</option>
                      ))}
                    </Input>
                  </FormGroup>
                </div>
              )}
            </div>
            <div className="inline-block mx-2">
              {isZatcaSubscribed && status ? (
                <>
                  {reportedList?.complied && <p className="mx-1 d-inline" style={getOutlineStyle('#72B566')}><Trans>Complied</Trans></p>}
                  {reportedList?.reported && <p className="mx-1 d-inline clickable" style={getOutlineStyle('#54984A')} onClick={() => setErrorWindow(true)}><Trans>Reported</Trans>
                    {reportedList?.updated_at && <span className="small"> ({formatDateTime(reportedList.updated_at)})<i className="fa fa-eye ml-1"></i></span>}
                  </p>}
                  {reportedList?.cleared && <p className="mx-1 d-inline clickable" style={getOutlineStyle('#8BD07C')} onClick={() => setErrorWindow(true)}><Trans>Cleared</Trans>
                    {reportedList?.updated_at && <span className="small"> ({formatDateTime(reportedList.updated_at)})<i className="fa fa-eye ml-1"></i></span>}
                  </p>}
                </>
              ) :
                reportedList?.updated_at ?
                  <span style={getOutlineStyle('#FFC107')} className="my-0 clickable" onClick={() => setErrorWindow(true)}><Trans>Not Reported</Trans><i className="fa fa-eye ml-1"></i></span>
                  :
                  <span style={getOutlineStyle('#FFC107')} className="my-0">----------</span>}
            </div>
            <div className="inline-block">
              {!!item.CreditNoteID && (
                <div>
                  <FormGroup>
                    <Input
                      type="select"
                      name="select"
                      className="py-0"
                      onChange={(e) => setSimpleType(e.target.value === 'true')}
                      disabled={item.DocumentTypeConfirmed}
                      value={useSimpleType}
                    >
                      <option value={true}>
                        {t('Export: Simplified Invoice')}
                      </option>
                      <option value={false}>
                        {t('Export: Tax Invoice')}
                      </option>
                    </Input>
                  </FormGroup>
                </div>
              )}
            </div>
          </div>
          }
        </div>

        <DocumentRenderer
          component={componentDoc} type={"creditNoteType"} name={formattedType}
        />
        <XeroErrors />


        <div className="actions-wrapper row justify-content-between">
          <div className="col-lg-12 col-xl-4 row flex-grow-0 justify-content-start mb-md-2 px-4">
            {!!item.CreditNoteID &&
              <>
                {item.SentToContact && (
                  <div className="col flex-grow-0 px-0">
                    <div className="d-flex align-items-center" style={{ paddingBottom: 20 }}>
                      <div className="text-success small">
                        <span className="fa fa-check mr-1" /> <Trans>Sent to contact</Trans>
                      </div>
                    </div>
                  </div>
                )}

                {isZatcaSubscribed && CanReport && item.Type === "ACCRECCREDIT" &&
                  <div className="col flex-grow-0 px-0">
                    {!isSending ?
                      <Dropdown size="sm" color="primary" isOpen={dropdownOpen} toggle={toggleDropdown} disabled={isSending}>
                        <DropdownToggle caret><Trans>Report to Zatca</Trans></DropdownToggle>
                        <DropdownMenu>
                          {/* <DropdownItem onClick={() => checkZatcaResend(() => preLocalChecking((updated_data) => preHandleSDKPost({ ...updated_data, reporting: false }), false))}>
                            <Trans>Send to Compliance</Trans>
                          </DropdownItem> */}
                          {!(reportedList?.reported || reportedList?.cleared) &&
                            <DropdownItem onClick={() => checkZatcaResend(() => preLocalChecking((updated_data) => preHandleSDKPost({ ...updated_data, reporting: true }), true))}>
                              <Trans>Send to {useSimpleType ? "Clearance" : "Reporting"}</Trans>
                            </DropdownItem>
                          }
                          {reportedList?.response &&
                            <DropdownItem onClick={() => { setErrorWindow(true); }}>
                              <Trans>View Response</Trans>
                            </DropdownItem>
                          }
                          <DropdownItem onClick={() => { preGetInvoiceXML(creditNoteData) }}>
                            <Trans>Download as XML</Trans>
                          </DropdownItem>
                        </DropdownMenu>
                      </Dropdown> :
                      <Button disabled><span className="fa fa-spinner fa-spin" /> <Trans>Sending</Trans></Button>}
                  </div>
                }
                {item.Attachments && (
                  <div className="col flex-grow-0 px-0">
                    <Attachments
                      xero={xero}
                      hideIncludeOnline
                      onAttachmentsChanged={handleAttachmentsChanged}
                      baseUrl={`creditnotes/${item.CreditNoteID}`}
                      attachments={item.Attachments}
                    />
                  </div>
                )}
              </>
            }
          </div>

          <div className="col-lg-12 col-xl-8 row px-4 justify-content-end">
            {!!item.CreditNoteID &&
              <>
                {/* <div className="col px-1 flex-grow-0">
                  <DownloadDocument simplified={useSimpleType} />
                </div> */}

                <div className="col px-1 flex-grow-0">
                  <DownloadCell simplified={useSimpleType} componentDoc={componentDoc} selectedOrganisation={selectedOrganisation} validation={preLocalChecking} setDownloadHandler={setDownloadHandler} i={{ ...items, ...creditNoteData, DocumentType: creditNoteType }} />
                </div>
                <div className="col px-1 flex-grow-0">
                  <EmailCell simplified={useSimpleType} componentDoc={componentDoc} selectedOrganisation={selectedOrganisation} validation={preLocalChecking} setDownloadHandler={setDownloadHandler} i={{ ...items, ...creditNoteData, DocumentType: creditNoteType }} />
                </div>
                <div className="col px-1 flex-grow-0">
                  <PrintCell simplified={useSimpleType} componentDoc={selectedPrintCell} i={item} selectedOrganisation={selectedOrganisation} />
                </div>
                <div className="col px-1 flex-grow-0">
                  <ShareCell simplified={useSimpleType} componentDoc={componentDoc} selectedOrganisation={selectedOrganisation} validation={preLocalChecking} setDownloadHandler={setDownloadHandler} i={{ ...items, ...creditNoteData, DocumentType: creditNoteType }} />
                </div>
                {(item.Status === "DRAFT" || item.Status === "SUBMITTED") && (
                  <div className="col px-1 flex-grow-0">
                    <Link
                      type="button"
                      className="btn-sm btn btn-primary mx-0"
                      to={`/${view_link}/credit_notes/edit/${item.CreditNoteID}`}>
                      <Trans>Edit Credit Note</Trans>
                    </Link>
                  </div>
                )}
                {(item.Status === "PAID" || item.Status === "AUTHORISED") && (
                  <div className="col px-1 flex-grow-0">
                    <CreditNoteOptions />
                  </div>
                )}
                {item.Status === "AUTHORISED" && (
                  <div className="col px-1 flex-grow-0">
                    <div className="btn-group">
                      <button
                        type="button"
                        className="btn-sm btn btn-primary mx-0"
                        onClick={togglePaymentVisibility}>
                        {isPaymentVisible ? t('Hide Payment') : t('Add Payment')}
                      </button>
                    </div>
                  </div>
                )}
              </>
            }
          </div>
        </div>
        <LocalForm className="mt-3 inv-table"
          onSubmit={onSubmit}
          validators={{
            Contact: { required },
            Date: { required },
            "LineItems[].Description": { required },
          }}
        >
          <Row>
            <Col {...cStyle.common}>
              <FormGroup
                label={payable ? "From" : "To"}
                model=".Contact">
                <Link to={`/contacts/view/${item.Contact.ContactID}`} {...cStyle.status}>
                  {item.Contact.Name}
                </Link>
              </FormGroup>
            </Col>
            <Col {...cStyle.common}>
              <FormGroup label="Status">
                <span className="view-form-group">{getStatus(item.Status)}</span>
              </FormGroup>
            </Col>
            <Col {...cStyle.common}>
              <FormGroup label="Date" model=".Date">
                <span className="view-form-group">{formatDate(item.Date) || "---"}</span>
              </FormGroup>
            </Col>
            <Col {...cStyle.common}>
              <FormGroup label={isPayable ? "Reference" : "Credit Note #"} model=".CreditNoteNumber">
                <span className="view-form-group">{item.CreditNoteNumber || '---'}</span>
              </FormGroup>
            </Col>
            <Col {...cStyle.common}>
              <FormGroup label="Type" model=".Type">
                <span className="view-form-group">{getType(item.Type || '---')}</span>
              </FormGroup>
            </Col>
            <Col {...cStyle.common}>
              <FormGroup label={"Reference"} model=".Reference">
                <span className="view-form-group">{items.Reference || '---'}</span>
              </FormGroup>
            </Col>
            {!isPayable && <Col {...cStyle.common}>
              <FormGroup label="Reason (required for KSA)" model=".Reason">
                <span className="view-form-group">{item.Reason || '---'}</span>
              </FormGroup>
            </Col>}
          </Row>
          <hr />
          <LineItemsView
            item={item}
            dispatch={dispatch}
            xero={xero}
            hideAddLines={true}
            hideAccountAndTax={true}
            removeLine={false}
          />
          <HistoryComponent historyItem={credHistory?.HistoryRecords} type="Credit Note" t={t} />
        </LocalForm>
      </Page>
      {isPaymentVisible && (
        <div ref={pageTakeOver} className="centered-container">
          <Payment
            creditNote={item}
            organisation={organisation}
            onPaymentAdded={handlePaymentAdded}
            xero={xero}
            paymentType="Refund"
          />
        </div>
      )}
      <NoTaxComponent formattedType={formattedType} invoiceSDKZatca={creditNoteData} selectedZatcaApp={selectedZatcaApp} setSelectedZatcaApp={setSelectedZatcaApp}></NoTaxComponent>
      <TaxExemptComponent />
      <ZatcaAppsComponent selectedZatcaApp={selectedZatcaApp} callbackFunc={callbackFunc} />

      {items &&
        <Modal isOpen={missingModalOpen} toggle={missingModalToggle} contentLabel="File Upload Modal">
          <ModalHeader toggle={missingModalToggle}><Trans>Missing Data</Trans></ModalHeader>
          <ModalBody>
            <Alert><p><Trans>Please completed the missing details so it can comply with the requirements of</Trans> <Trans><strong>Zatca</strong></Trans></p></Alert>
            {!(items.Allocations[0]?.Invoice || items.AllocationInvoice) &&
              <Row className="RelatedInvoice">
                <Col >
                  <FormGroup label={t("Allocated Invoice Number")}>
                    <Input type="text" placeholder={`${t("Select an invoice")}`} value={missingData.AllocationInvoice} onChange={(e) => { setMissingData({ ...missingData, AllocationInvoice: e.target.value }) }} />
                  </FormGroup>
                </Col>
              </Row>
            }
            {!items.Reason &&
              <Row className="Reason">
                <Col>
                  <FormGroup label="Reason">
                    <Input
                      type="text"
                      name="Reason"
                      value={missingData.Reason}
                      onChange={(e) => { setMissingData({ ...missingData, Reason: e.target.value }) }}
                      placeholder={t("Reason")}
                    />
                  </FormGroup>
                </Col>
              </Row>
            }
          </ModalBody>
          <ModalFooter>
            <Button color="primary" onClick={handleMissingSave}><Trans>Save</Trans></Button>
            <Button color="secondary" onClick={missingModalToggle}><Trans>Close</Trans></Button>
          </ModalFooter>
        </Modal>}

      {/* Resend Modal */}
      <MainResendModal title={t('Resend Confirmation')} action={t("Confirm")}>
        <Alert color="warning">
          <Trans>This invoice is already reported to Zatca, Do you want to report again?</Trans>
        </Alert>
      </MainResendModal>
      {errorWindow && reportedList?.response && <ErrorDetailsComponent show={errorWindow} handleClose={() => { setErrorWindow(false); }} details={reportedList.response} typeOfInv={formattedType} showWarnings={true} reload={() => { }} />}
    </div>
  )
};

const enhance = compose(
  withOrganisationContextConsumer,
  withPropsOnChange(["payable"], ({ payable }) => {
    const type = payable ? "ACCPAYCREDIT" : "ACCRECCREDIT";
    return {
      type,
      payable: payable,
    };
  }),
  withReload(),
  onMount(({ match, xero, location, type, fetchOrganisation }) => async () => {
    const { id } = match.params;
    const item = await xero
      .get(`creditnotes/${match.params.id}`)
      .get("data")
      .then((x) => ({
        ...x,
        LineItems: x.LineItems.map((i) => ({
          ...i,
          _id: shortid(),
        })),
        hasZeroTax: x?.LineItems.some((i) => (i.TaxAmount || 0) === 0),
      }));
    const credHistory = await xero
      .get(`creditnotes/${match.params.id}/History`)
      .get("data")
      .then((credHistory) => ({
        ...credHistory,
      }));

    const organisation = await fetchOrganisation();
    return {
      item,
      credHistory,
      organisation,
    };
  }),
  withLocalForm({
    initialState: ({ item }) => item,
    stateKey: "item",
    formKey: "form",
  }),
  withContactSearch(),
  withXeroErrors(),
  withOptions(),
  withHandlers({
    handleAttachmentsChanged:
      ({ xero, match, dispatch }) =>
        () => {
          xero
            .get(`creditnotes/${match.params.id}/attachments`)
            .get("data")
            .tap((d) => dispatch(actions.change("local.Attachments", d)));
        },
    handlePaymentAdded:
      ({ reload }) =>
        () => {
          reload();
        },
  }),
  withDocumentRenderer({
    onDownload:
      ({ item }) =>
        (getDocument) =>
          getDocument(
            {
              item,
            },
            `creditnote_${item.CreditNoteNumber}.pdf`
          ),
  })
);

export default enhance(ViewCreditNote);
